import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

export default function Header() {
  const [scrolled, setScrolled] = useState(false)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [boatsDropdownOpen, setBoatsDropdownOpen] = useState(false)
  const location = useLocation()

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20)
    }
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileMenuOpen(false)
    setBoatsDropdownOpen(false)
  }, [location.pathname])

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  const isBoatsActive =
    isActive('/baade') ||
    isActive('/motorbaade') ||
    isActive('/motorsejlere') ||
    isActive('/sejlbaade') ||
    location.pathname.startsWith('/baad/')

  return (
    <header className="sticky top-0 z-50 transition-all duration-300 bg-white shadow-md border-b border-slate-200">
      {/* Top Utility Bar with Contact Info & Badge */}
      <div className="bg-slate-900 text-white text-xs border-b border-slate-800 py-1.5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 sm:gap-6 text-slate-300">
            <a
              href="tel:40207034"
              className="flex items-center gap-1.5 hover:text-[#2BABE3] transition-colors whitespace-nowrap font-medium"
            >
              <i className="fa-solid fa-phone text-[#2BABE3] text-xs" aria-hidden="true" />
              <span>40 20 70 34</span>
              <span className="hidden sm:inline text-[10px] text-slate-400 font-normal ml-1">(svarer hele døgnet)</span>
            </a>
            <span className="text-slate-700 hidden sm:inline">|</span>
            <a
              href="mailto:info@nordfyn-marine.dk"
              className="hidden sm:flex items-center gap-1.5 hover:text-[#2BABE3] transition-colors whitespace-nowrap font-medium"
            >
              <i className="fa-solid fa-envelope text-[#2BABE3] text-xs" aria-hidden="true" />
              <span>info@nordfyn-marine.dk</span>
            </a>
          </div>

          <div className="flex items-center gap-2 text-[10px] font-semibold text-[#2BABE3] uppercase tracking-wider whitespace-nowrap">
            <i className="fa-solid fa-shield-halved text-[#2BABE3]" aria-hidden="true" />
            <span>Solgt eller gratis</span>
          </div>
        </div>
      </div>

      {/* Main Bar with Logo & CTA + Burger Menu Button */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3 flex items-center justify-between gap-4">
          {/* Logo */}
          <Link to="/" className="flex items-center shrink-0 group py-0.5">
            <img
              src="https://assets.auxodev.dk/assets/7090fdb0-7f55-4b6b-b1f9-a2c2b33a9494/nordfyn-marine-logo.png"
              alt="Nordfyn Marine"
              className="h-10 sm:h-12 md:h-14 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105"
            />
          </Link>

          {/* Right Action Buttons: "Sælg din båd" + Responsive Menu Button */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <Link
              to="/saelg-din-baad"
              className="inline-flex items-center justify-center gap-1.5 border border-[#1E7BC1] bg-[#1E7BC1] hover:bg-[#2BABE3] hover:border-[#2BABE3] text-white px-3 py-2 sm:px-4 sm:py-2.5 text-[10px] sm:text-xs uppercase font-semibold tracking-wider transition-all rounded-xl whitespace-nowrap shadow-sm"
            >
              <i className="fa-solid fa-tag text-[10px]" aria-hidden="true" />
              <span>Sælg din båd</span>
            </Link>

            {/* Burger Toggle button for < 1200px screens */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="[@media(min-width:1200px)]:hidden inline-flex items-center gap-2 px-3 py-2 sm:px-3.5 sm:py-2.5 rounded-xl text-slate-800 hover:text-[#1E7BC1] hover:bg-slate-100 focus:outline-none transition-colors border border-slate-300 bg-slate-50 font-semibold text-xs tracking-wider uppercase"
              aria-label="Toggle navigation menu"
            >
              <i
                className={`fa-solid ${
                  mobileMenuOpen ? 'fa-xmark' : 'fa-bars'
                } text-base`}
                aria-hidden="true"
              />
              <span className="hidden sm:inline font-bold">Menu</span>
            </button>
          </div>
        </div>
      </div>

      {/* Main Navigation Row (Desktop >= 1200px) */}
      <div className="hidden [@media(min-width:1200px)]:block bg-slate-50/90 border-t border-slate-100 py-2">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
          <nav className="flex items-center gap-2 xl:gap-4">
            <Link
              to="/"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/') && location.pathname === '/'
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Forside
            </Link>

            {/* Dropdown Menu for "Både" */}
            <div
              className="relative py-1.5"
              onMouseEnter={() => setBoatsDropdownOpen(true)}
              onMouseLeave={() => setBoatsDropdownOpen(false)}
            >
              <Link
                to="/baade"
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all inline-flex items-center gap-1.5 ${
                  isBoatsActive
                    ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                    : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
                }`}
              >
                <span>Både</span>
                <i
                  className={`fa-solid fa-chevron-down text-[9px] transition-transform duration-200 ${
                    boatsDropdownOpen ? 'rotate-180' : ''
                  }`}
                  aria-hidden="true"
                />
              </Link>

              {/* Dropdown Box */}
              {boatsDropdownOpen && (
                <div className="absolute top-full left-0 w-56 pt-1 z-50">
                  <div className="bg-white border border-slate-200 shadow-2xl py-1 rounded-2xl overflow-hidden">
                    <Link
                      to="/baade"
                      className="block px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-900 hover:text-[#2BABE3] hover:bg-slate-50 transition-colors border-b border-slate-100"
                    >
                      <i className="fa-solid fa-ship text-[#2BABE3] mr-2 text-[9px]" />
                      Alle både til salg
                    </Link>
                    <Link
                      to="/motorbaade"
                      className="block px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] font-medium text-slate-700 hover:text-[#2BABE3] hover:bg-slate-50 transition-colors"
                    >
                      <i className="fa-solid fa-anchor text-[#2BABE3] mr-2 text-[9px]" />
                      Motorbåde
                    </Link>
                    <Link
                      to="/motorsejlere"
                      className="block px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] font-medium text-slate-700 hover:text-[#2BABE3] hover:bg-slate-50 transition-colors"
                    >
                      <i className="fa-solid fa-compass text-[#2BABE3] mr-2 text-[9px]" />
                      Motorsejlere
                    </Link>
                    <Link
                      to="/sejlbaade"
                      className="block px-4 py-2.5 text-[10px] uppercase tracking-[0.15em] font-medium text-slate-700 hover:text-[#2BABE3] hover:bg-slate-50 transition-colors"
                    >
                      <i className="fa-solid fa-wind text-[#2BABE3] mr-2 text-[9px]" />
                      Sejlbåde
                    </Link>
                  </div>
                </div>
              )}
            </div>

            <Link
              to="/forespoergsel"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/forespoergsel')
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Forespørgsel
            </Link>

            <Link
              to="/saelg-din-baad"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/saelg-din-baad')
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Sælg din båd
            </Link>

            <Link
              to="/forsikring"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/forsikring')
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Forsikring
            </Link>

            <Link
              to="/samarbejdspartnere"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/samarbejdspartnere')
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Samarbejdspartnere
            </Link>

            <Link
              to="/om-os"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/om-os')
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Om os
            </Link>

            <Link
              to="/kontakt"
              className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.15em] font-medium transition-all ${
                isActive('/kontakt')
                  ? 'text-[#1E7BC1] border-b-2 border-[#2BABE3] font-bold'
                  : 'text-slate-800 hover:text-[#1E7BC1] hover:bg-white'
              }`}
            >
              Kontakt
            </Link>
          </nav>
        </div>
      </div>

      {/* Mobile & Tablet Navigation Drawer (Below 1200px) */}
      {mobileMenuOpen && (
        <div className="[@media(min-width:1200px)]:hidden bg-white border-t border-slate-200 px-4 py-4 space-y-2 animate-fadeIn shadow-2xl max-h-[85vh] overflow-y-auto">
          <Link
            to="/"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/') && location.pathname === '/'
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-house text-[#1E7BC1] mr-3 text-sm" />
            Forside
          </Link>

          {/* Mobile Dropdown for Både */}
          <div className="rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => setBoatsDropdownOpen(!boatsDropdownOpen)}
              className={`w-full text-left px-4 py-3 text-base font-semibold flex justify-between items-center transition-colors ${
                isBoatsActive
                  ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                  : 'text-slate-800 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center">
                <i className="fa-solid fa-ship text-[#1E7BC1] mr-3 text-sm" />
                Både til salg
              </span>
              <i
                className={`fa-solid fa-chevron-down text-sm transition-transform duration-200 ${
                  boatsDropdownOpen ? 'rotate-180' : ''
                }`}
                aria-hidden="true"
              />
            </button>

            {boatsDropdownOpen && (
              <div className="pl-6 pr-2 py-2 space-y-1 bg-slate-50 border-l-2 border-[#2BABE3] my-1 rounded-r-xl">
                <Link
                  to="/baade"
                  className="block px-3 py-2 rounded-lg text-sm font-semibold text-slate-900 hover:text-[#1E7BC1]"
                >
                  <i className="fa-solid fa-list mr-2 text-xs text-[#2BABE3]" />
                  Alle både til salg
                </Link>
                <Link
                  to="/motorbaade"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-[#1E7BC1]"
                >
                  <i className="fa-solid fa-anchor mr-2 text-xs text-[#2BABE3]" />
                  Motorbåde
                </Link>
                <Link
                  to="/motorsejlere"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-[#1E7BC1]"
                >
                  <i className="fa-solid fa-compass mr-2 text-xs text-[#2BABE3]" />
                  Motorsejlere
                </Link>
                <Link
                  to="/sejlbaade"
                  className="block px-3 py-2 rounded-lg text-sm font-medium text-slate-700 hover:text-[#1E7BC1]"
                >
                  <i className="fa-solid fa-wind mr-2 text-xs text-[#2BABE3]" />
                  Sejlbåde
                </Link>
              </div>
            )}
          </div>

          <Link
            to="/forespoergsel"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/forespoergsel')
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-clipboard-question text-[#1E7BC1] mr-3 text-sm" />
            Forespørgsel
          </Link>

          <Link
            to="/saelg-din-baad"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/saelg-din-baad')
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-tag text-[#1E7BC1] mr-3 text-sm" />
            Sælg din båd
          </Link>

          <Link
            to="/forsikring"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/forsikring')
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-shield-halved text-[#1E7BC1] mr-3 text-sm" />
            Forsikring
          </Link>

          <Link
            to="/samarbejdspartnere"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/samarbejdspartnere')
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-handshake text-[#1E7BC1] mr-3 text-sm" />
            Samarbejdspartnere
          </Link>

          <Link
            to="/om-os"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/om-os')
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-circle-info text-[#1E7BC1] mr-3 text-sm" />
            Om os
          </Link>

          <Link
            to="/kontakt"
            className={`block px-4 py-3 rounded-xl text-base font-semibold ${
              isActive('/kontakt')
                ? 'bg-slate-100 text-[#1E7BC1] border-l-4 border-[#2BABE3]'
                : 'text-slate-800 hover:bg-slate-50'
            }`}
          >
            <i className="fa-solid fa-envelope text-[#1E7BC1] mr-3 text-sm" />
            Kontakt
          </Link>

          <div className="pt-4 border-t border-slate-200 flex flex-col sm:flex-row gap-3">
            <a
              href="tel:40207034"
              className="flex-1 flex items-center justify-center gap-2 bg-[#1E7BC1] hover:bg-[#2BABE3] text-white py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors shadow-sm"
            >
              <i className="fa-solid fa-phone text-white" aria-hidden="true" />
              <span>Ring 40 20 70 34</span>
            </a>
            <a
              href="mailto:info@nordfyn-marine.dk"
              className="flex-1 flex items-center justify-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-800 py-3 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors border border-slate-300"
            >
              <i className="fa-solid fa-envelope text-[#1E7BC1]" aria-hidden="true" />
              <span>info@nordfyn-marine.dk</span>
            </a>
          </div>
        </div>
      )}
    </header>
  )
}
